import {
    PrimaryButton,
    SecondaryButton,
} from '@acko-tech/building-blocks.ui.button';
import {
    OrderStatus,
    isDealerCancellableOrderStatus,
} from 'components/dashboard/transactions/constants';
import { DeviceCategory } from 'components/newPlan/constants';
import { useFetchParams } from 'hooks/useFetchParams';
import SnackBar from 'library/snackbar';
import { useContext, useEffect, useState } from 'react';

// utils
import { useViewport } from 'utils/responsive';
import { LOB, Permissions } from 'utils/constants';
import { MPosContext } from 'utils/context';
import { getDomainOrigin } from 'utils/serverConfig';
import { sendCustomerPolicyCommunication } from './api';
import * as Styles from './styles';
import { getAllPaymentOptions } from 'components/orderSummary/apis';
import {
    IPaymentOption,
    PaymentOptionType,
} from 'components/orderSummary/types';

// events
import {
    SegmentEventNameTypes,
    SegmentEventPages,
    SegmentEventPageTypes,
    SegmentEventActionNames,
    SegmentEventActionTypes,
} from 'utils/segment/constants';
import sendSegmentEvent from 'utils/segment';
import { useRouter } from 'next/router';

interface IProps {
    deviceName: string;
    lastUpdatedAt: string;
    status: string;
    statusMessage: string;
    serialNumber: string;
    planName: string;
    coverage: string;
    mobileNumber: string;
    amount: number;
    deviceCategory: string;
    onClick: any;
    isBtnLoading: boolean;
    invoice_url: string;
    vin?: string;
    policyNumber?: string;
    policyEKey?: string;
    paymentPaidBy?: string;
    orderEKey?: string;
}

const TransactionDetailsModalV2: React.FC<IProps> = (props) => {
    const {
        deviceName,
        lastUpdatedAt,
        status,
        statusMessage,
        serialNumber,
        planName,
        coverage,
        mobileNumber,
        amount,
        deviceCategory,
        onClick,
        isBtnLoading,
        vin,
        invoice_url,
        policyNumber,
        policyEKey,
        paymentPaidBy,
        orderEKey,
    } = props;
    const { lob } = useFetchParams();
    const [isCommunicationProcessing, setIsCommunicationProcession] =
        useState<boolean>(false);
    const [isOrderCancelling, setIsOrderCancelling] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });
    const [paymentOptions, setPaymentOptions] = useState<IPaymentOption[]>([]);
    const { permissionsMap, userDetails, partnerId, permissions } =
        useContext(MPosContext);
    const { isMobile } = useViewport();
    const router = useRouter();

    const getColors = () => {
        switch (status) {
            case OrderStatus.ORDER_CREATED:
            case OrderStatus.PAYMENT_INITIATED_BY_CUSTOMER:
            case OrderStatus.PAYMENT_INITIATED_BY_DEALER:
            case OrderStatus.PAYMENT_SUCCESS_BY_CUSTOMER:
            case OrderStatus.PAYMENT_SUCCESS_BY_DEALER:
                return {
                    color: '#5920C5',
                    background: '#EFE9FB',
                };
            case OrderStatus.POLICY_CREATED:
                return {
                    background: '#99fc9d',
                    color: '#0c843f',
                };
            default:
                return {
                    color: '#D83D37',
                    background: '#FBEAEA',
                };
        }
    };

    const { background, color } = getColors();

    const sendCommunicationHandler = async () => {
        if (policyNumber) {
            setIsCommunicationProcession(true);
            try {
                const isSent = await sendCustomerPolicyCommunication(
                    policyNumber
                );
                if (isSent) {
                    setSnackbar({
                        message: 'Communication Sent Successfully',
                        type: 'success',
                    });
                }
            } catch (e) {
                setSnackbar({
                    message: 'Failed to Send Communication',
                    type: 'failure',
                });
            } finally {
                setIsCommunicationProcession(false);
            }
        }
    };

    const cancelOrderHandler = async () => {
        setIsOrderCancelling(true);
        await onClick('cancel');
        setIsOrderCancelling(false);
    };

    const customerPDFUrl = `${getDomainOrigin()}/api/mpos/policy/pdf?pid=${policyEKey}`;

    useEffect(() => {
        const fetchPaymentOptions = async () => {
            const { status, data, statusCode } = await getAllPaymentOptions(
                partnerId,
                permissions.group
            );
            if (statusCode === 401) {
                router.push('/login');
                setSnackbar({
                    type: 'failure',
                    message: 'Token expired. Redirecting to login page.',
                });
                return;
            }
            if (status) {
                setPaymentOptions(data || []);
            }
        };

        fetchPaymentOptions();

        if (typeof window !== 'undefined') {
            sendSegmentEvent(
                SegmentEventNameTypes.VIEW,
                {
                    page_type: SegmentEventPageTypes.MAIN_PAGE,
                    view_name: SegmentEventPages.VIEW_TRANSACTION_DETAIL_MODAL,
                },
                {
                    userDetails,
                    isMobile,
                }
            );
        }
    }, [window, partnerId]);

    const onResendLinkClick = () => {
        onClick('resend');

        // segment Events
        sendSegmentEvent(
            SegmentEventNameTypes.ACTION,
            {
                action_name: SegmentEventActionNames.CLICK_RESEND_LINK_BUTTON,
                action_type: SegmentEventActionTypes.TAP_BTN,
            },
            {
                userDetails,
                isMobile,
            }
        );
    };

    const onPayClick = () => {
        const isDealerWalletPaymentApplicable = paymentOptions.some(
            (option) =>
                option?.type === PaymentOptionType.DEALER_WALLET_PAYMENT &&
                option?.applicable === true
        );

        if (isDealerWalletPaymentApplicable && partnerId) {
            router.push(
                `/${lob}/${partnerId}/payment-mode?order_ekey=${orderEKey}`
            );
            return;
        }

        onClick('pay');
    };

    return (
        <Styles.Wrapper>
            <Styles.ModalName>{deviceName}</Styles.ModalName>
            {status === OrderStatus.POLICY_CREATED ? (
                <Styles.LastUpdatedDate>
                    Policy Issued On {lastUpdatedAt}
                </Styles.LastUpdatedDate>
            ) : (
                <Styles.LastUpdatedDate>
                    Last updated {lastUpdatedAt}
                </Styles.LastUpdatedDate>
            )}
            {status === OrderStatus.POLICY_CREATED &&
                paymentPaidBy?.toLowerCase() === 'customer' && (
                    <Styles.StatusWrapper>
                        <Styles.StatusText
                            background={background}
                            color={color}
                        >
                            Paid by customer
                        </Styles.StatusText>
                    </Styles.StatusWrapper>
                )}

            <Styles.StatusWrapper>
                <Styles.StatusText background={background} color={color}>
                    {statusMessage || status}
                </Styles.StatusText>

                {status === OrderStatus.POLICY_CREATED &&
                    permissionsMap[Permissions.GST_INVOICE] && (
                        <Styles.InvoiceWrapper>
                            <Styles.InvoiceUrl
                                href={invoice_url}
                                target="_blank"
                            >
                                Download B2B Invoice
                            </Styles.InvoiceUrl>
                        </Styles.InvoiceWrapper>
                    )}
                {status === OrderStatus.POLICY_CREATED &&
                    policyEKey &&
                    permissionsMap[Permissions.CUSTOMER_PDF] && (
                        <Styles.InvoiceWrapper>
                            <Styles.InvoiceUrl
                                href={customerPDFUrl}
                                target="_blank"
                            >
                                Customer PDF
                            </Styles.InvoiceUrl>
                        </Styles.InvoiceWrapper>
                    )}
            </Styles.StatusWrapper>

            {status === OrderStatus.POLICY_CREATED && policyNumber && (
                <Styles.StatusWrapper>
                    <Styles.SendCommunicationButton
                        onClick={sendCommunicationHandler}
                        disabled={isCommunicationProcessing}
                    >
                        Send Customer Communication
                    </Styles.SendCommunicationButton>
                </Styles.StatusWrapper>
            )}
            <Styles.PlanDetailsWrapper>
                <Styles.RowWrapper>
                    <Styles.LabelText>Device model</Styles.LabelText>
                    <Styles.ValueText>{deviceName}</Styles.ValueText>
                </Styles.RowWrapper>
                <Styles.Hr />
                {lob === LOB.EMBEDDED && (
                    <>
                        <Styles.RowWrapper>
                            <Styles.LabelText>
                                {deviceCategory === DeviceCategory.MOBILE ||
                                deviceCategory === DeviceCategory.TABLET
                                    ? 'IMEI'
                                    : 'Serial Number'}
                            </Styles.LabelText>
                            <Styles.ValueText>{serialNumber}</Styles.ValueText>
                        </Styles.RowWrapper>
                        <Styles.Hr />
                        <Styles.RowWrapper>
                            <Styles.LabelText>Plan Name</Styles.LabelText>
                            <Styles.ValueText>{planName}</Styles.ValueText>
                        </Styles.RowWrapper>
                        <Styles.Hr />
                        <Styles.RowWrapper>
                            <Styles.LabelText>What’s covered</Styles.LabelText>
                            <Styles.ValueText>{coverage}</Styles.ValueText>
                        </Styles.RowWrapper>{' '}
                    </>
                )}
                {lob === LOB.AUTO && (
                    <>
                        <Styles.RowWrapper>
                            <Styles.LabelText>VIN</Styles.LabelText>
                            <Styles.ValueText>{vin}</Styles.ValueText>
                        </Styles.RowWrapper>
                        <Styles.Hr />
                        <Styles.RowWrapper>
                            <Styles.LabelText>Plan Name</Styles.LabelText>
                            <Styles.ValueText>{planName}</Styles.ValueText>
                        </Styles.RowWrapper>
                    </>
                )}
            </Styles.PlanDetailsWrapper>
            <Styles.CustomerDetailsWrapper>
                <Styles.CustomerMobileFlex>
                    <Styles.CustomerMobileText>
                        Customer mobile number
                    </Styles.CustomerMobileText>
                    <Styles.CustomerMobileNumber>
                        {mobileNumber}
                    </Styles.CustomerMobileNumber>
                </Styles.CustomerMobileFlex>
                <Styles.CustomerAmountFlex>
                    <Styles.CustomerMobileFlex>
                        <Styles.CustomerMobileText>
                            Amount
                        </Styles.CustomerMobileText>
                        <Styles.CustomerMobileNumber>
                            ₹{amount}
                        </Styles.CustomerMobileNumber>
                    </Styles.CustomerMobileFlex>
                </Styles.CustomerAmountFlex>
            </Styles.CustomerDetailsWrapper>
            {(status === OrderStatus.ORDER_CREATED ||
                status === OrderStatus.PAYMENT_INITIATED_BY_DEALER ||
                status === OrderStatus.PAYMENT_FAILED) &&
                paymentOptions.length > 0 && (
                    <Styles.ResendLinkWrapper>
                        <PrimaryButton
                            loading={isBtnLoading}
                            isFullWidth={true}
                            size={'medium'}
                            onClick={onPayClick}
                        >
                            Pay Acko
                        </PrimaryButton>
                    </Styles.ResendLinkWrapper>
                )}
            {isDealerCancellableOrderStatus(status) && (
                <Styles.ResendLinkWrapper>
                    <SecondaryButton
                        loading={isOrderCancelling}
                        isFullWidth={true}
                        size={'medium'}
                        onClick={cancelOrderHandler}
                    >
                        Cancel Order
                    </SecondaryButton>
                </Styles.ResendLinkWrapper>
            )}
            {(status === OrderStatus.CUSTOMER_LINK_SHARED ||
                status === OrderStatus.PAYMENT_INITIATED_BY_CUSTOMER) && (
                <Styles.ResendLinkWrapper>
                    <PrimaryButton
                        loading={isBtnLoading}
                        isFullWidth={true}
                        size={'medium'}
                        onClick={onResendLinkClick}
                    >
                        Resend Link
                    </PrimaryButton>
                </Styles.ResendLinkWrapper>
            )}
            {/* {selectedTab === TabOptions.PENDING &&
                status !== OrderStatus.ORDER_LINK_EXPIRED &&
                status !== OrderStatus.PAYMENT_SUCCESS_BY_DEALER && (
                    <Styles.ResendLinkWrapper>
                        <PrimaryButton
                            loading={isBtnLoading}
                            isFullWidth={true}
                            size={'medium'}
                            onClick={() =>
                                onClick(
                                    status === OrderStatus.ORDER_CREATED
                                        ? 'resend'
                                        : 'pay_acko'
                                )
                            }
                        >
                            {status === OrderStatus.ORDER_CREATED
                                ? 'Resend link'
                                : 'Pay Acko'}
                        </PrimaryButton>
                    </Styles.ResendLinkWrapper>
                )} */}
            {/* {selectedTab === TabOptions.PENDING &&
                status !== OrderStatus.ORDER_CREATED &&
                status !== OrderStatus.ORDER_LINK_EXPIRED && (
                    <Styles.ResendLinkText onClick={() => onClick('resend')}>
                        Resend link
                    </Styles.ResendLinkText>
                )} */}
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </Styles.Wrapper>
    );
};

export default TransactionDetailsModalV2;
